import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Route, Loader2, ArrowLeft, RefreshCw, Printer, Trash2, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import RunPreview from "@/components/scheduler/RunPreview";
import RunDisruptionPanel from "@/components/scheduler/RunDisruptionPanel";

export default function RunDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canAny } = useAuth();
  const canWrite = canAny(["operations.schedule", "operations.manage"]);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [disr, setDisr] = useState(null);
  const [disrBusy, setDisrBusy] = useState(false);

  const load = useCallback(() => api.get(`/runs/${id}`).then((r) => setRun(r.data)).catch(() => setRun(false)), [id]);
  const loadScan = useCallback(() => {
    setDisrBusy(true);
    return api.post(`/runs/${id}/incidents/refresh`).then((r) => setDisr(r.data)).catch(() => setDisr(false)).finally(() => setDisrBusy(false));
  }, [id]);
  useEffect(() => { load(); loadScan(); }, [load, loadScan]);

  const recalc = async () => {
    setBusy(true);
    try { const r = await api.put(`/runs/${id}`); toast.success(`Recalculated${r.data.unassigned.length ? ` · ${r.data.unassigned.length} unassigned` : ""}`); load(); loadScan(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not recalculate"); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this run? The shifts are kept and simply unlinked.")) return;
    try { await api.delete(`/runs/${id}`); toast.success("Run deleted"); navigate("/runs"); }
    catch { toast.error("Could not delete run"); }
  };

  if (run === null) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading run…</div>;
  if (run === false) return <div className="p-10 text-slate-500">Run not found.</div>;

  return (
    <div className="p-5 lg:p-8 w-full max-w-[1800px] mx-auto" data-testid="page-run-detail">
      <button onClick={() => navigate("/runs")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-4" data-testid="run-detail-back">
        <ArrowLeft className="w-4 h-4" /> All runs
      </button>
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Route className="w-6 h-6" /></span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{run.name}</h1>
            <p className="text-sm text-slate-500 mt-0.5 font-mono">{run.run_id} · {(run.days || []).length} days · created by {run.created_by}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.open(`/runs/${id}/print`, "_blank")} className="gap-1.5" data-testid="run-print-btn"><Printer className="w-4 h-4" /> Print day sheets</Button>
          {canWrite && <Button onClick={recalc} disabled={busy} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="run-recalc-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Recalculate</Button>}
          {canWrite && <Button variant="outline" onClick={del} className="gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="run-delete-btn"><Trash2 className="w-4 h-4" /></Button>}
        </div>
      </div>

      {run.needs_recalc && (
        <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="run-needs-recalc">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-bold text-amber-900">This run needs recalculating</div>
            <p className="text-sm text-amber-800 mt-0.5">{(run.changed_shifts || []).length} linked shift(s) changed address, duration, appointment or date since this run was planned. Recalculate to refresh the schedule and ETAs.</p>
          </div>
        </div>
      )}

      <RunDisruptionPanel runId={id} data={disr} busy={disrBusy} initialBriefing={run.disruption_briefing} alerts={run.disruption_alerts} onRefresh={loadScan} />
      <RunPreview preview={run.preview} scan={disr && disr.scan} />
    </div>
  );
}
